import React, { Component } from 'react';
import { Button, Container, Typography, Paper, Box, Stack, Divider } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';

// Key for storing refresh attempts in localStorage
const ERROR_ATTEMPTS_KEY = 'error_boundary_attempts';
const MAX_ATTEMPTS = 1;

// This wrapper is needed because error boundaries must be class components,
// but we need hooks to access navigation
function ErrorBoundaryWithNavigation(props) {
    return <ErrorBoundary {...props} />;
}

// Loading spinner component
function LoadingSpinner() {
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999
        }}>
            <div style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                animation: 'spin 1s linear infinite'
            }}></div>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

// Error display component using actual MUI components
function ErrorDisplay({ error, resetErrorBoundary }) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                bgcolor: 'grey.50',
                py: 4
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        borderRadius: 2,
                        textAlign: 'center'
                    }}
                >
                    <Box
                        sx={{
                            mb: 3,
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                bgcolor: 'primary.main',
                                borderRadius: '50%',
                                p: 2,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <ErrorOutlineIcon fontSize="large" />
                        </Box>
                    </Box>

                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            color: 'primary.main'
                        }}
                    >
                        Oops! Something went wrong
                    </Typography>

                    <Typography
                        variant="body1"
                        color="text.secondary"
                        paragraph
                        sx={{ mb: 3 }}
                    >
                        We encountered an error while loading this page. Our team has been notified and is working on a fix.
                    </Typography>

                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 3,
                            bgcolor: 'grey.50',
                            textAlign: 'left'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box
                                sx={{
                                    bgcolor: 'error.main',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    mr: 1
                                }}
                            />
                            <Typography variant="subtitle2" color="text.primary">
                                Error Details
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                                fontFamily: '"Roboto Mono", monospace',
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                                overflowWrap: 'break-word',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {error && error.toString()}
                        </Typography>
                    </Paper>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="center"
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => window.location.href = '/'}
                            startIcon={<HomeIcon />}
                        >
                            Go to Homepage
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                if (resetErrorBoundary) resetErrorBoundary();
                                else window.location.reload();
                            }}
                            startIcon={<RefreshIcon />}
                        >
                            Try Again
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}


class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            isNavigating: false,
            showErrorUI: false
        };
        this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
    }

    getLocation() {
        return {
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash
        };
    }

    // Get current attempt count from localStorage
    getAttemptCount() {
        try {
            const attempts = localStorage.getItem(ERROR_ATTEMPTS_KEY);
            return attempts ? parseInt(attempts, 10) : 0;
        } catch (e) {
            return 0;
        }
    }

    // Increment attempt count
    incrementAttemptCount() {
        try {
            const currentAttempts = this.getAttemptCount();
            localStorage.setItem(ERROR_ATTEMPTS_KEY, (currentAttempts + 1).toString());
            return currentAttempts + 1;
        } catch (e) {
            return 1;
        }
    }

    // Reset attempt count
    resetAttemptCount() {
        try {
            localStorage.removeItem(ERROR_ATTEMPTS_KEY);
        } catch (e) {
            // Ignore errors when clearing
        }
    }

    resetErrorBoundary() {
        this.resetAttemptCount();
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            isNavigating: false,
            showErrorUI: false
        });
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // You can log the error to an error reporting service
        console.error("Error caught by ErrorBoundary:", error, errorInfo);
        this.setState({
            error: error,
            errorInfo: errorInfo,
            isNavigating: true  // Show the spinner
        });

        // Track attempt count and handle navigation
        setTimeout(() => {
            const attempts = this.incrementAttemptCount();
            console.log(`Error recovery attempt: ${attempts} of ${MAX_ATTEMPTS}`);

            // Check if we're on the login page
            const location = this.getLocation();
            const isLoginPage = location.pathname === '/login';

            if (attempts >= MAX_ATTEMPTS) {
                if (isLoginPage) {
                    // On login page, show error UI instead of redirecting
                    console.log("Error on login page, showing error UI");
                    this.setState({
                        isNavigating: false,
                        showErrorUI: true
                    });
                } else {
                    // After max attempts, navigate to login page and reset counter
                    this.resetAttemptCount();
                    console.log("Max attempts reached, redirecting to login page");

                    sessionStorage.clear();
                    localStorage.clear();
                    window.location.href = '/login';
                }
            } else {
                // Just refresh the page
                window.location.reload();
            }
        }, 1000);
    }

    render() {
        if (this.state.hasError) {
            if (this.state.showErrorUI) {
                return <ErrorDisplay error={this.state.error} resetErrorBoundary={this.resetErrorBoundary} />;
            }
            return this.state.isNavigating ? <LoadingSpinner /> : null;
        }

        // If there's no error, render children normally
        return this.props.children;
    }
}

export default ErrorBoundaryWithNavigation;